import React, { useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import JobOpportunityClient, { JobOpportunityListView } from "../../jobOpportunity/data/api/JobOpportunityClient";
import JobApplicationClient from "../../jobApplications/data/api/JobApplicationClient";
import LearnerClient from '../data/api/LearnerClient';
import Loader from '../../../shared/components/Loader';
import Alert from '../../../shared/components/Alert';
import { useLearnerContext } from '../data/context/LearnerContext';
import axios from 'axios';
import '../styles/LearnerStyles.css';
import { Country, State as StateCity } from 'country-state-city';
import Select from 'react-select';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { User, Briefcase, FileText, ShieldCheck, DollarSign, FileEdit, Link2, Globe } from 'lucide-react';
import DateTimePicker from '../../../shared/components/DateTimePicker';
import strings from '../../../utils/Strings';

const APPLICANT_SOURCES = [
  "LinkedIn", "Referral", "Job portal", "Website", "Agency", "Walk-in", "Internal", "Other"
];
const MAX_LEARNERS = 5;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const REMOTE_OPTIONS = ['Fully Remote', 'Hybrid', 'Remote Optional'];

const DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_DOCUMENT_SIZE_MB = 5;
const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;

const DOC_TYPES = {
  passport: { label: 'Passport' },
  drivers_license: { label: "Driver's License" },
  visa: { label: 'Visa' },
  aadhaar: { label: 'Aadhaar' },
  pan: { label: 'PAN Card' },
  ssn: { label: 'Social Security Number' },
  bank: { label: 'Bank Document' },
} as const;
type DocType = keyof typeof DOC_TYPES;

const formatSSN = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DOC_ROWS: { docType: DocType; numberLabel: string; hasExpiry: boolean }[] = [
  { docType: 'passport', numberLabel: 'Passport Number', hasExpiry: true },
  { docType: 'drivers_license', numberLabel: 'License Number', hasExpiry: true },
  { docType: 'visa', numberLabel: 'Visa Type', hasExpiry: true },
  { docType: 'aadhaar', numberLabel: 'Aadhaar Number', hasExpiry: false },
  { docType: 'pan', numberLabel: 'PAN Number', hasExpiry: false },
  { docType: 'ssn', numberLabel: 'SSN', hasExpiry: false },
];

const DocUploadField: React.FC<{
  id: string;
  label: string;
  file: File | null;
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
}> = ({ id, label, file, onSelect, onClear }) => (
  <div className="al-field">
    <label>{label}</label>
    <div className="al-resume-picker">
      <input type="file" id={id} accept={DOC_ACCEPT} style={{ display: 'none' }} onChange={onSelect} />
      <label htmlFor={id} className="al-resume-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '11px' }}>
        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
        </svg>
        {file ? 'Change File' : 'Attach Document'}
      </label>
      {file && (
        <div className="al-resume-info">
          <svg width="14" height="14" fill="none" stroke="#059669" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{file.name}</span>
          <button className="al-resume-clear" onClick={onClear} title="Remove file">
            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {!file && <span className="al-hint">PDF/JPG/PNG up to {MAX_DOCUMENT_SIZE_MB}MB</span>}
    </div>
  </div>
);

const GENDER_OPTIONS = [
  { label: 'Prefer Not To Say', value: 'Prefer Not To Say' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Others', value: 'Others' },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EDUCATION_OPTIONS = [
  { label: 'Select Qualification', value: '' },
  { label: 'Under Graduate (UG)', value: 'UG' },
  { label: 'Post Graduate (PG)', value: 'PG' },
];

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    border: state.isFocused ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
    color: 'var(--text-heading)',
    background: 'var(--surface)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--primary-ghost)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--primary)' : 'var(--border)'
    },
    outline: 'none',
    transition: 'border-color var(--transition), box-shadow var(--transition)',
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  singleValue: (base: any, state: any) => ({ ...base, color: state.isDisabled ? 'var(--text-muted)' : 'var(--text-heading)' }),
  input: (base: any) => ({
    ...base,
    color: 'var(--text-heading)',
    margin: '0',
    padding: '0',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    minHeight: 'auto',
    height: 'auto',
    'input:focus': {
      boxShadow: 'none',
      border: 'none',
      outline: 'none'
    }
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any) => ({ ...base, padding: '4px' }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, { isFocused, isSelected }: any) => ({
    ...base,
    backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--primary-light)' : 'var(--surface)',
    color: isSelected ? '#fff' : 'var(--text-heading)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: '#fff'
    }
  })
};

interface DocUploadState {
  file: File | null;
  key: string;
}

interface IdentityDetails {
  passport_number: string;
  expiry_date: string;
  document_url: string;
}

interface BankDetails {
  bank_name: string;
  account_number: string;
  routing_number: string;
  account_type: string;
  branch_name: string;
  ifsc_code: string;
  document_url: string;
}

interface LearnerFormData {
  id: string;
  activeTab: 'personal' | 'professional' | 'documents' | 'financials' | 'notes';
  isEducationDropdown: boolean;
  first_name: string;
  last_name: string;
  email_address: string;
  phone: string;
  status: 'Active' | 'Inactive';
  gender: string;
  education: string;
  date_of_birth: string;
  country: string;
  state: string;
  year_of_experience: number | '';
  work_preference: string;
  professional_certifications: string[];
  expected_ctc: string;
  resumeFile: File | null;
  resumeKey: string;
  identity: {
    passport: IdentityDetails;
    drivers_license: IdentityDetails;
    visa: IdentityDetails;
    aadhaar: IdentityDetails;
    pan: IdentityDetails;
    ssn: IdentityDetails;
  };
  bank: BankDetails;
  note: string;
  job_opportunity_id: string;
  source: string;
  docUploads: Record<DocType, DocUploadState>;
  errors: Record<string, string>;
}

const emptyIdentityDetails = (): IdentityDetails => ({
  passport_number: '',
  expiry_date: '',
  document_url: '',
});

const emptyLearner = (): LearnerFormData => ({
  id: Math.random().toString(36).slice(2),
  activeTab: 'personal',
  isEducationDropdown: true,
  first_name: '',
  last_name: '',
  email_address: '',
  phone: '',
  status: 'Active',
  gender: 'Prefer Not To Say',
  education: '',
  date_of_birth: '',
  country: '',
  state: '',
  year_of_experience: '',
  work_preference: '',
  professional_certifications: [],
  expected_ctc: '',
  resumeFile: null,
  resumeKey: '',
  identity: {
    passport: emptyIdentityDetails(),
    drivers_license: emptyIdentityDetails(),
    visa: emptyIdentityDetails(),
    aadhaar: emptyIdentityDetails(),
    pan: emptyIdentityDetails(),
    ssn: emptyIdentityDetails(),
  },
  bank: {
    bank_name: '',
    account_number: '',
    routing_number: '',
    account_type: '',
    branch_name: '',
    ifsc_code: '',
    document_url: ''
  },
  note: '',
  job_opportunity_id: '',
  source: 'Other',
  docUploads: {
    passport: { file: null, key: '' },
    drivers_license: { file: null, key: '' },
    visa: { file: null, key: '' },
    aadhaar: { file: null, key: '' },
    pan: { file: null, key: '' },
    ssn: { file: null, key: '' },
    bank: { file: null, key: '' },
  },
  errors: {},
});

const formatDob = (raw: string, prev: string): string => {
  // Remove all non-digits
  const digits = raw.replace(/\D/g, '');
  // Build formatted string enforcing DD/MM/YYYY
  let out = '';
  if (digits.length <= 2) {
    out = digits;
  } else if (digits.length <= 4) {
    out = digits.slice(0, 2) + '/' + digits.slice(2);
  } else {
    out = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4, 8);
  }
  return out;
};

const EditableTagInput: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}> = ({ label, items, onChange, disabled, placeholder }) => {
  const [inputVal, setInputVal] = useState('');

  const addItem = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    if (items.includes(trimmed)) return;
    onChange([...items, trimmed]);
    setInputVal('');
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addItem(); }
  };

  return (
    <div className="al-field al-field-full" style={{ display: 'flex', flexDirection: 'column', height: '100%', marginBottom: 0 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        <Briefcase size={14} /> {label}
      </label>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '6px 12px',
          background: disabled ? '#f8fafc' : '#fff',
          borderRadius: '8px',
          border: '1px solid #cbd5e1',
          minHeight: '44px',
          height: '100%',
          flex: 1,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          cursor: disabled ? 'not-allowed' : 'text',
          transition: 'border-color var(--transition), box-shadow var(--transition)'
        }}
        onClick={(e) => {
          if (!disabled && e.target === e.currentTarget) {
            document.getElementById(`tag-input-${label.replace(/\s+/g, '')}`)?.focus();
          }
        }}
        className={!disabled ? "tag-input-container" : ""}
      >
        {items && items.length > 0 ? (
          items.map((item, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: disabled ? '#e2e8f0' : 'rgba(5, 150, 105, 0.1)', color: disabled ? '#64748b' : 'var(--primary)', border: disabled ? '1px solid #cbd5e1' : '1px solid rgba(5, 150, 105, 0.2)', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>
              {item}
              {!disabled && (
                <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, fontSize: '17px', transform: 'translateY(-1px)' }} title={`Remove ${item}`}>
                  ×
                </button>
              )}
            </span>
          ))
        ) : null}
        <input
          id={`tag-input-${label.replace(/\s+/g, '')}`}
          className="tag-input-field"
          type="text"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addItem}
          placeholder={items && items.length === 0 ? placeholder : ''}
          disabled={disabled}
          style={{
            flex: 1,
            minWidth: '120px',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            padding: 0,
            height: '24px',
            lineHeight: '24px',
            fontSize: '14px',
            color: 'var(--text-color)'
          }}
        />
      </div>
    </div>
  );
};

const validateLearner = (l: LearnerFormData): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!l.first_name.trim()) errors.first_name = 'First name is required';
  if (!l.last_name.trim()) errors.last_name = 'Last name is required';
  if (!l.email_address.trim()) {
    errors.email_address = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(l.email_address)) {
    errors.email_address = 'Enter a valid email address';
  }
  if (l.date_of_birth && l.date_of_birth.length !== 10) {
    errors.date_of_birth = 'Use format DD/MM/YYYY';
  }
  return errors;
};

const AddLearnerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnTo || '/dashboard/learners';
  useLearnerContext();
  const [learners, setLearners] = useState<LearnerFormData[]>([emptyLearner()]);

  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);


  React.useEffect(() => {
    const fetchJobs = async () => {
      setJobsLoading(true);
      try {
        const data = await JobOpportunityClient.listOpportunities({ page_number: 1, limit: 1000, status: 'Open' });
        setJobs(data.records || []);
      } catch (err) {
        console.error("Failed to load jobs", err);
      } finally {
        setJobsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const [saving, setSaving] = useState(false);
  const [uploadingResumes, setUploadingResumes] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const update = useCallback((id: string, field: keyof LearnerFormData, value: any) => {
    setLearners(prev => prev.map(l => l.id === id ? { ...l, [field]: value, errors: { ...l.errors, [field]: '' } } : l));
  }, []);

  const handleDob = useCallback((id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLearners(prev => prev.map(l => {
      if (l.id !== id) return l;
      const formatted = formatDob(raw, l.date_of_birth);
      return { ...l, date_of_birth: formatted, errors: { ...l.errors, date_of_birth: '' } };
    }));
  }, []);

  const handleDobKeyDown = useCallback((id: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Block non-digit non-control keys
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  }, []);

  const handleResumeSelect = useCallback((id: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLearners(prev => prev.map(l => l.id === id ? { ...l, resumeFile: file, resumeKey: '' } : l));
  }, []);

  const handleDocSelect = useCallback((id: string, docType: DocType, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(pdf|jpg|jpeg|png)$/)) {
      setErrorMsg(`${DOC_TYPES[docType].label} must be a PDF, JPG, or PNG file.`);
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setErrorMsg(`${DOC_TYPES[docType].label} exceeds the ${MAX_DOCUMENT_SIZE_MB}MB size limit.`);
      return;
    }
    setLearners(prev => prev.map(l => l.id === id
      ? { ...l, docUploads: { ...l.docUploads, [docType]: { file, key: '' } } }
      : l));
  }, []);

  const updateIdentity = useCallback((id: string, docType: keyof LearnerFormData['identity'], field: keyof IdentityDetails, value: string) => {
    setLearners(prev => prev.map(l => l.id === id
      ? { ...l, identity: { ...l.identity, [docType]: { ...l.identity[docType], [field]: value } } }
      : l));
  }, []);

  const updateBank = useCallback((id: string, field: keyof BankDetails, value: string) => {
    setLearners(prev => prev.map(l => l.id === id
      ? { ...l, bank: { ...l.bank, [field]: value } }
      : l));
  }, []);

  const addLearner = () => {
    if (learners.length < MAX_LEARNERS) {
      setLearners(prev => [...prev, emptyLearner()]);
    }
  };

  const setActiveTab = (id: string, tab: LearnerFormData['activeTab']) => {
    setLearners(prev => prev.map(l => l.id === id ? { ...l, activeTab: tab } : l));
  };

  const removeLearner = (id: string) => {
    setLearners(prev => prev.filter(l => l.id !== id));
  };

  const uploadResume = async (file: File): Promise<string> => {
    const data = await LearnerClient.createUploadUrl(file.name);
    const { uploadURL, key, download_url, downloadURL } = data;
    await axios.put(uploadURL, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return download_url || downloadURL || key;
  };

  const buildIdentityPayload = (identity: LearnerFormData['identity'], docUploads: LearnerFormData['docUploads']) => ({
    passport_details: {
      passport_number: identity.passport.passport_number || undefined,
      expiry_date: identity.passport.expiry_date || undefined,
      document_url: docUploads.passport.key || undefined,
    },
    drivers_license_details: {
      license_number: identity.drivers_license.passport_number || undefined,
      expiry_date: identity.drivers_license.expiry_date || undefined,
      document_url: docUploads.drivers_license.key || undefined,
    },
    visa_details: {
      visa_type: identity.visa.passport_number || undefined,
      expiry_date: identity.visa.expiry_date || undefined,
      document_url: docUploads.visa.key || undefined,
    },
    aadhaar_details: {
      aadhaar_number: identity.aadhaar.passport_number || undefined,
      document_url: docUploads.aadhaar.key || undefined,
    },
    pan_card_details: {
      pan_number: identity.pan.passport_number || undefined,
      document_url: docUploads.pan.key || undefined,
    },
    ssn_details: {
      ssn: identity.ssn.passport_number || undefined,
      document_url: docUploads.ssn.key || undefined,
    },
  });

  const handleSave = async () => {
    // Validate all
    let hasErrors = false;
    const validated = learners.map(l => {
      const errors = validateLearner(l);
      if (Object.keys(errors).length > 0) hasErrors = true;
      return { ...l, errors };
    });
    setLearners(validated);
    if (hasErrors) return;

    try {
      setSaving(true);

      // Upload resumes first
      setUploadingResumes(true);
      const withKeys = await Promise.all(
        validated.map(async (l) => {
          if (l.resumeFile) {
            const key = await uploadResume(l.resumeFile);
            return { ...l, resumeKey: key };
          }
          return l;
        })
      );
      setUploadingResumes(false);

      // Upload documents
      const withDocs = await Promise.all(
        withKeys.map(async (l) => {
          const docUploads = { ...l.docUploads };
          await Promise.all(
            (Object.keys(DOC_TYPES) as DocType[]).map(async (dt) => {
              const entry = docUploads[dt];
              if (entry.file) {
                const key = await uploadResume(entry.file);
                docUploads[dt] = { file: entry.file, key };
              }
            })
          );
          return { ...l, docUploads: docUploads };
        })
      );

      // Build payload
      const payload = withDocs.map(l => ({
        first_name: l.first_name.trim(),
        last_name: l.last_name.trim(),
        email_address: l.email_address.trim().toLowerCase(),
        status: l.status === 'Inactive' ? 'InActive' : l.status,
        gender: l.gender,
        education: l.education || undefined,
        phone: l.phone || undefined,
        date_of_birth: l.date_of_birth || undefined,
        country: l.country || undefined,
        state: l.state || undefined,
        year_of_experience: typeof l.year_of_experience === 'number' ? l.year_of_experience : undefined,
        work_preference: l.work_preference || undefined,
        professional_certifications: l.professional_certifications || undefined,
        expected_ctc: l.expected_ctc || undefined,
        resume_url: l.resumeKey || undefined,
        job_opportunity_id: l.job_opportunity_id || undefined,
        source: l.source || 'Other',
        ...buildIdentityPayload(l.identity, l.docUploads),
        bank_account_details: {
          bank_name: l.bank.bank_name || undefined,
          account_number: l.bank.account_number || undefined,
          routing_number: l.bank.routing_number || undefined,
          account_type: l.bank.account_type || undefined,
          branch_name: l.bank.branch_name || undefined,
          ifsc_code: l.bank.ifsc_code || undefined,
          document_url: l.docUploads.bank?.key || undefined,
        },
        note: l.note || undefined,
        onboarded: false,
      }));

      const response = await LearnerClient.addMultipleLearners(payload);



      const generateCategoryBlock = (title: string, emails: string[], hexColor: string) => {
        if (!emails || emails.length === 0) return '';
        return `
          <div style="background: var(--surface); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; margin-bottom: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: rgba(0,0,0,0.02); border-bottom: 1px solid var(--border);">
              <span style="font-weight: 600; color: var(--text-heading); font-size: 0.9rem;">${title}</span>
              <span style="background: ${hexColor}15; color: ${hexColor}; padding: 3px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 700; border: 1px solid ${hexColor}30;">${emails.length}</span>
            </div>
            <div style="padding: 8px 14px; max-height: 140px; overflow-y: auto;">
              ${emails.map((email: string) => `<div style="font-size: 0.85rem; color: var(--text-color); padding: 5px 0; display: flex; align-items: center; gap: 10px;">
                <div style="width: 6px; height: 6px; border-radius: 50%; background: ${hexColor}; flex-shrink: 0;"></div>
                <span style="word-break: break-all; font-family: monospace; font-size: 0.8rem;">${email}</span>
              </div>`).join('')}
            </div>
          </div>
        `;
      };

      const msg = `
        <div style="display: flex; flex-direction: column; gap: 8px; text-align: center; max-height: 50vh; overflow-y: auto; padding-right: 4px; overflow-x: hidden;">
          <p style="margin: 0 0 12px 0; font-size: 0.95rem; color: var(--text-heading); font-weight: 500;">${response.detail || 'Applicants processed successfully.'}</p>
          <div style="text-align: left;">
            ${generateCategoryBlock('Added Successfully', response.added, '#10B981')}
            ${generateCategoryBlock('Skipped (Already Exists)', response.skipped, '#F59E0B')}
            ${generateCategoryBlock('Failed to Add', response.failed, '#EF4444')}
          </div>
        </div>
      `;

      setSuccessMessage(msg);
      setSuccessAlert(true);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.detail || 'Failed to add learners. Please try again.');
    } finally {
      setSaving(false);
      setUploadingResumes(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const completedCount = learners.length;

  return (
    <>
      <Loader
        isLoading={saving}
        message={uploadingResumes ? (learners.length > 1 ? 'Uploading resumes...' : 'Uploading resume...') : (learners.length > 1 ? 'Saving applicants...' : 'Saving applicant...')}
      />
      {/* Success Alert */}
      <Alert
        show={successAlert}
        title={learners.length > 1 ? "Applicants Processed" : "Applicant Processed"}
        message={successMessage}
        type="success"
        primaryButtonText="Cancel"
        onClose={() => {
          setSuccessAlert(false);
          setLearners([{ ...emptyLearner(), id: crypto.randomUUID() }]);
        }}
      />
      {/* Error Alert */}
      <Alert
        show={!!errorMsg}
        title="Error"
        message={errorMsg || ''}
        type="error"
        primaryButtonText="OK"
        onClose={() => setErrorMsg(null)}
      />

      <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>

        {/* ── Hero ── */}
        <div className="lp-hero" style={{ flexDirection: 'column', gap: '4px', marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div className="lp-hero-left">
              <button className="up-back-btn" onClick={() => navigate(returnUrl)} title="Back">
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l-7 7 7 7" />
                </svg>
              </button>
              <h1>Add Applicants</h1>
              <span className="lp-hero-chip">Add up to {MAX_LEARNERS} applicants at once</span>
            </div>
            <span className="al-count-badge">{learners.length} / {MAX_LEARNERS}</span>
          </div>
        </div>

        {/* ── Progress Dots ── */}
        <div className="al-progress-strip">
          {Array.from({ length: MAX_LEARNERS }).map((_, i) => (
            <div key={i} className={`al-progress-dot ${i < learners.length ? 'filled' : ''}`} />
          ))}
        </div>

        {/* ── Learner Cards ── */}
        <div className="al-cards-list">
          <AnimatePresence>
            {learners.map((l, idx) => (
              <motion.div
                key={l.id}
                className="al-card"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
              >
                {/* Card header */}
                <div className="al-card-head">
                  <div className="al-card-num">
                    <span>{idx + 1}</span>
                  </div>
                  <div className="al-card-head-text">
                    <h3>{l.first_name || `Applicant ${idx + 1}`}</h3>
                    <p>{l.email_address || 'Fill in the details below'}</p>
                  </div>
                  {/* Status Toggle */}
                  <div className="al-status-toggle-wrap">
                    <span className={`al-status-label ${l.status === 'Active' ? 'active' : 'inactive'}`}>
                      {l.status}
                    </span>
                    <button
                      className={`al-toggle ${l.status === 'Active' ? 'on' : 'off'}`}
                      onClick={() => update(l.id, 'status', l.status === 'Active' ? 'Inactive' : 'Active')}
                      title={`Toggle status (currently ${l.status})`}
                    >
                      <span className="al-toggle-knob" />
                    </button>
                  </div>
                  {learners.length > 1 && (
                    <button className="al-remove-btn" onClick={() => removeLearner(l.id)} title="Remove applicant">
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Tabs */}
                <div className="al-tabs" style={{ padding: '0 24px' }}>
                  <button type="button" className={`al-tab ${l.activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab(l.id, 'personal')}>Personal Info</button>
                  <button type="button" className={`al-tab ${l.activeTab === 'professional' ? 'active' : ''}`} onClick={() => setActiveTab(l.id, 'professional')}>Professional Details</button>
                  <button type="button" className={`al-tab ${l.activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab(l.id, 'documents')}>Documents</button>
                  <button type="button" className={`al-tab ${l.activeTab === 'financials' ? 'active' : ''}`} onClick={() => setActiveTab(l.id, 'financials')}>Financials</button>
                  <button type="button" className={`al-tab ${l.activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab(l.id, 'notes')}>Notes</button>
                </div>

                <div className="al-card-body">
                  {l.activeTab === 'personal' && (
                    <div className="al-form-grid">
                      {/* First Name */}
                      <div className={`al-field ${l.errors.first_name ? 'has-error' : ''}`}>
                        <label>First Name <span className="req">*</span></label>
                        <input
                          type="text"
                          placeholder="e.g. Priya"
                          value={l.first_name}
                          onChange={e => update(l.id, 'first_name', e.target.value)}
                        />
                        {l.errors.first_name && <span className="al-error">{l.errors.first_name}</span>}
                      </div>

                      {/* Last Name */}
                      <div className={`al-field ${l.errors.last_name ? 'has-error' : ''}`}>
                        <label>Last Name <span className="req">*</span></label>
                        <input
                          type="text"
                          placeholder="e.g. Sharma"
                          value={l.last_name}
                          onChange={e => update(l.id, 'last_name', e.target.value)}
                        />
                        {l.errors.last_name && <span className="al-error">{l.errors.last_name}</span>}
                      </div>

                      {/* Email */}
                      <div className={`al-field ${l.errors.email_address ? 'has-error' : ''}`}>
                        <label>Email Address <span className="req">*</span></label>
                        <input
                          type="email"
                          placeholder="e.g. priya@email.com"
                          value={l.email_address}
                          onChange={e => update(l.id, 'email_address', e.target.value)}
                        />
                        {l.errors.email_address && <span className="al-error">{l.errors.email_address}</span>}
                      </div>

                      {/* Gender */}
                      <div className="al-field">
                        <label>Gender</label>
                        <select value={l.gender} onChange={e => update(l.id, 'gender', e.target.value)}>
                          {GENDER_OPTIONS.map(g => (
                            <option key={g.value} value={g.value}>{g.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Phone */}
                      <div className="al-field">
                        <label>Phone Number</label>
                        <input
                          type="text"
                          placeholder="e.g. +91 9876543210"
                          value={l.phone}
                          onChange={e => update(l.id, 'phone', e.target.value)}
                        />
                      </div>

                      {/* Education */}
                      <div className="al-field">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ margin: 0 }}>Educational Qualification</label>
                          <button
                            type="button"
                            onClick={() => {
                              update(l.id, 'isEducationDropdown', !l.isEducationDropdown);
                              if (!l.isEducationDropdown && !strings.learner.degreesList.includes(l.education)) {
                                update(l.id, 'education', '');
                              }
                            }}
                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '11px', cursor: 'pointer', padding: 0, fontWeight: 600, textDecoration: 'underline' }}
                          >
                            {l.isEducationDropdown ? 'Enter Manually' : 'Select from List'}
                          </button>
                        </div>
                        {l.isEducationDropdown ? (
                          <Select
                            options={strings.learner.degreesList.map(deg => ({ value: deg, label: deg }))}
                            value={strings.learner.degreesList.includes(l.education) ? { value: l.education, label: l.education } : null}
                            onChange={(val: any) => update(l.id, 'education', val?.value || '')}
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                            placeholder="Select Qualification"
                          />
                        ) : (
                          <input
                            type="text"
                            placeholder="e.g. B.Tech Computer Science"
                            value={l.education}
                            onChange={e => update(l.id, 'education', e.target.value)}
                          />
                        )}
                      </div>

                      {/* Date of Birth */}
                      <div className={`al-field ${l.errors.date_of_birth ? 'has-error' : ''}`}>
                        <label>Date of Birth</label>
                        <input
                          type="text"
                          placeholder="DD/MM/YYYY"
                          value={l.date_of_birth}
                          onChange={e => handleDob(l.id, e)}
                          onKeyDown={e => handleDobKeyDown(l.id, e)}
                          maxLength={10}
                          inputMode="numeric"
                        />
                        {l.errors.date_of_birth && (
                          <span className="al-error">{l.errors.date_of_birth}</span>
                        )}
                      </div>

                      {/* Country */}
                      <div className="al-field">
                        <label>Country</label>
                        <Select
                          options={Country.getAllCountries().map(c => ({ value: c.name, label: c.name, code: c.isoCode }))}
                          value={Country.getAllCountries().map(c => ({ value: c.name, label: c.name, code: c.isoCode })).find(o => o.value === l.country) || null}
                          onChange={(val: any) => { update(l.id, 'country', val?.value || ''); update(l.id, 'state', ''); }}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition={'fixed'}
                          placeholder="Select country"
                        />
                      </div>

                      {/* State */}
                      <div className="al-field">
                        <label>State</label>
                        <Select
                          options={(() => {
                            const selectedCountryCode = Country.getAllCountries().find(c => c.name === l.country)?.isoCode;
                            if (!selectedCountryCode) return [];
                            return StateCity.getStatesOfCountry(selectedCountryCode).map(s => ({ value: s.name, label: s.name, code: s.isoCode }));
                          })()}
                          value={(() => {
                            const selectedCountryCode = Country.getAllCountries().find(c => c.name === l.country)?.isoCode;
                            if (!selectedCountryCode) return null;
                            return StateCity.getStatesOfCountry(selectedCountryCode)
                              .map(s => ({ value: s.name, label: s.name, code: s.isoCode }))
                              .find(o => o.value === l.state) || null;
                          })()}
                          onChange={(val: any) => update(l.id, 'state', val?.value || '')}
                          isDisabled={!l.country}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition={'fixed'}
                          placeholder="Select state"
                        />
                      </div>

                      <div className="al-field al-field-full">
                        <label>Resume <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 400 }}>(PDF only)</span></label>
                        <div className="al-resume-picker">
                          <input
                            type="file"
                            accept=".pdf"
                            style={{ display: 'none' }}
                            ref={el => { fileInputRefs.current[l.id] = el; }}
                            onChange={e => handleResumeSelect(l.id, e)}
                          />
                          <button
                            className="al-resume-btn"
                            style={{ fontSize: '11px' }}
                            onClick={() => fileInputRefs.current[l.id]?.click()}
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {l.resumeFile ? 'Change File' : 'Attach Resume'}
                          </button>
                          {l.resumeFile && (
                            <div className="al-resume-info">
                              <svg width="14" height="14" fill="none" stroke="#059669" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span>{l.resumeFile.name}</span>
                              <button
                                className="al-resume-clear"
                                onClick={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, resumeFile: null, resumeKey: '' } : ll))}
                                title="Remove file"
                              >
                                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                          {!l.resumeFile && <span className="al-hint">PDF accepted</span>}
                        </div>
                      </div>
                    </div>
                  )}

                  {l.activeTab === 'professional' && (
                    <div className="al-form-grid">
                      <div className="al-field-full" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', paddingBottom: '24px', marginBottom: '12px', borderBottom: '1px solid var(--border)' }}>
                        <div className="al-field">
                          <label>Years of Experience</label>
                          <input
                            type="number"
                            step="0.1"
                            placeholder="e.g. 3.5"
                            value={l.year_of_experience}
                            onChange={e => update(l.id, 'year_of_experience', e.target.value !== '' ? parseFloat(e.target.value) : '')}
                          />
                        </div>

                        <div className="al-field">
                          <label>Work Preference</label>
                          <select
                            value={l.work_preference}
                            onChange={e => update(l.id, 'work_preference', e.target.value)}
                            style={{
                              width: '100%',
                              height: '40px',
                              border: '1.5px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              padding: '0 12px',
                              fontSize: '0.85rem',
                              color: 'var(--text-heading)',
                              backgroundColor: 'var(--surface)',
                              outline: 'none'
                            }}
                          >
                            <option value="">Select</option>
                            <option value="Hybrid">Hybrid</option>
                            <option value="Onsite">Onsite</option>
                            <option value="Remote">Remote</option>
                          </select>
                        </div>

                        <div className="al-field">
                          <label>Expected CTC</label>
                          <input
                            type="text"
                            value={l.expected_ctc}
                            onChange={e => update(l.id, 'expected_ctc', e.target.value)}
                          />
                        </div>

                        <div className="al-field">
                          <label>Job Opportunity (Optional)</label>
                          <select
                            value={l.job_opportunity_id}
                            onChange={(e) => update(l.id, 'job_opportunity_id', e.target.value)}
                            style={{
                              width: '100%',
                              height: '40px',
                              border: '1.5px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              padding: '0 12px',
                              fontSize: '0.85rem',
                              color: 'var(--text-heading)',
                              backgroundColor: 'var(--surface)',
                              outline: 'none'
                            }}
                          >
                            <option value="">None</option>
                            {jobs.map(job => (
                              <option key={job.opportunity_id} value={job.opportunity_id}>{job.title}</option>
                            ))}
                          </select>
                        </div>

                        <div className="al-field">
                          <label>Applicant Source</label>
                          <select
                            value={l.source}
                            onChange={(e) => update(l.id, 'source', e.target.value)}
                            style={{
                              width: '100%',
                              height: '40px',
                              border: '1.5px solid var(--border)',
                              borderRadius: 'var(--radius-md)',
                              padding: '0 12px',
                              fontSize: '0.85rem',
                              color: 'var(--text-heading)',
                              backgroundColor: 'var(--surface)',
                              outline: 'none'
                            }}
                          >
                            {APPLICANT_SOURCES.map(source => (
                              <option key={source} value={source}>{source}</option>
                            ))}
                          </select>
                        </div>
                      </div>


                      <div className="al-field-full">
                        <EditableTagInput
                          label="Professional Certifications"
                          items={l.professional_certifications}
                          onChange={(val: string[]) => update(l.id, 'professional_certifications', val)}
                          placeholder="e.g. AWS Certified..."
                        />
                      </div>
                    </div>
                  )}

                  {l.activeTab === 'documents' && (
                    <div className="al-form-grid" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

                      {/* Passport */}
                      <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#7c3aed', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>PASSPORT</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Passport Number</label>
                            <input type="text" placeholder="e.g. P1234567" value={l.identity.passport.passport_number} onChange={e => updateIdentity(l.id, 'passport', 'passport_number', e.target.value)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Expiry Date</label>
                            <DateTimePicker dateOnly placeholder="dd - mm - yyyy" value={l.identity.passport.expiry_date} onChange={(val: string) => updateIdentity(l.id, 'passport', 'expiry_date', val)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-passport-${l.id}`}
                              label="Document"
                              file={l.docUploads.passport.file}
                              onSelect={e => handleDocSelect(l.id, 'passport', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, passport: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Driver's License */}
                      <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#059669', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>DRIVER'S LICENSE</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>License Number</label>
                            <input type="text" placeholder="e.g. DL123456" value={l.identity.drivers_license.passport_number} onChange={e => updateIdentity(l.id, 'drivers_license', 'passport_number', e.target.value)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Expiry Date</label>
                            <DateTimePicker dateOnly placeholder="dd - mm - yyyy" value={l.identity.drivers_license.expiry_date} onChange={(val: string) => updateIdentity(l.id, 'drivers_license', 'expiry_date', val)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-drivers-license-${l.id}`}
                              label="Document"
                              file={l.docUploads.drivers_license.file}
                              onSelect={e => handleDocSelect(l.id, 'drivers_license', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, drivers_license: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Visa */}
                      <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f172a', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>VISA</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Visa Type / Status</label>
                            <input type="text" placeholder="e.g. H-1B, Student Visa" value={l.identity.visa.passport_number} onChange={e => updateIdentity(l.id, 'visa', 'passport_number', e.target.value)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Expiry Date</label>
                            <DateTimePicker dateOnly placeholder="dd - mm - yyyy" value={l.identity.visa.expiry_date} onChange={(val: string) => updateIdentity(l.id, 'visa', 'expiry_date', val)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-visa-${l.id}`}
                              label="Document"
                              file={l.docUploads.visa.file}
                              onSelect={e => handleDocSelect(l.id, 'visa', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, visa: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Aadhaar */}
                      <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a855f7', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>AADHAAR</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>Aadhaar Number</label>
                            <input type="text" placeholder="XXXX XXXX XXXX" maxLength={14} value={l.identity.aadhaar.passport_number} onChange={e => updateIdentity(l.id, 'aadhaar', 'passport_number', e.target.value)} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-aadhaar-${l.id}`}
                              label="Document"
                              file={l.docUploads.aadhaar.file}
                              onSelect={e => handleDocSelect(l.id, 'aadhaar', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, aadhaar: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* PAN Card */}
                      <div className="al-field-full" style={{ padding: '24px 0', borderBottom: '1px dashed var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#eab308', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>PAN CARD</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>PAN Number</label>
                            <input type="text" placeholder="e.g. ABCDE1234F" maxLength={10} value={l.identity.pan.passport_number} onChange={e => updateIdentity(l.id, 'pan', 'passport_number', e.target.value.toUpperCase())} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-pan-${l.id}`}
                              label="Document"
                              file={l.docUploads.pan.file}
                              onSelect={e => handleDocSelect(l.id, 'pan', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, pan: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* SSN */}
                      <div className="al-field-full" style={{ padding: '24px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', marginBottom: '16px' }}>
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                          <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>SOCIAL SECURITY NUMBER</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <label>SSN</label>
                            <input type="text" placeholder="XXX-XX-XXXX" maxLength={11} value={l.identity.ssn.passport_number} onChange={e => updateIdentity(l.id, 'ssn', 'passport_number', formatSSN(e.target.value))} />
                          </div>
                          <div className="al-field" style={{ marginBottom: 0 }}>
                            <DocUploadField
                              id={`edit-doc-ssn-${l.id}`}
                              label="Document"
                              file={l.docUploads.ssn.file}
                              onSelect={e => handleDocSelect(l.id, 'ssn', e)}
                              onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, ssn: { file: null, key: '' } } } : ll))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {l.activeTab === 'financials' && (
                    <div className="al-form-grid">
                      <div className="al-field">
                        <label>Bank Name</label>
                        <input type="text" placeholder="e.g. State Bank of India" value={l.bank.bank_name} onChange={e => updateBank(l.id, 'bank_name', e.target.value)} />
                      </div>
                      <div className="al-field">
                        <label>Account Number</label>
                        <input type="text" placeholder="e.g. 1234567890" value={l.bank.account_number} onChange={e => updateBank(l.id, 'account_number', e.target.value)} />
                      </div>
                      <div className="al-field">
                        <label>Routing Number</label>
                        <input type="text" placeholder="e.g. 021000021" value={l.bank.routing_number} onChange={e => updateBank(l.id, 'routing_number', e.target.value)} />
                      </div>
                      <div className="al-field">
                        <label>Branch Address</label>
                        <input type="text" placeholder="e.g. Main Branch" value={l.bank.branch_name} onChange={e => updateBank(l.id, 'branch_name', e.target.value)} />
                      </div>
                      <div className="al-field">
                        <label>IFSC Code</label>
                        <input type="text" placeholder="e.g. SBIN0001234" value={l.bank.ifsc_code} onChange={e => updateBank(l.id, 'ifsc_code', e.target.value.toUpperCase())} />
                      </div>
                      <div className="al-field">
                        <label>Account Type</label>
                        <select value={l.bank.account_type} onChange={e => updateBank(l.id, 'account_type', e.target.value)}>
                          <option value="">Select type</option>
                          <option value="Savings">Savings</option>
                          <option value="Current">Current</option>
                          <option value="Checking">Checking</option>
                        </select>
                      </div>
                      <div className="al-field">
                        <DocUploadField
                          id={`edit-doc-bank-${l.id}`}
                          label="Bank Document"
                          file={l.docUploads.bank?.file || null}
                          onSelect={(e) => handleDocSelect(l.id, 'bank', e)}
                          onClear={() => setLearners(prev => prev.map(ll => ll.id === l.id ? { ...ll, docUploads: { ...ll.docUploads, bank: { file: null, key: '' } } } : ll))}
                        />
                      </div>
                    </div>
                  )}

                  {l.activeTab === 'notes' && (
                    <div style={{ margin: '0 -24px -24px -24px' }}>
                      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: '4px' }}>Admin Notes</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Internal summary or remarks about this applicant (not visible to the applicant).</div>
                      </div>
                      <div style={{ padding: '24px' }}>
                        <textarea
                          placeholder="Add any internal notes, observations, or summary about this applicant..."
                          value={l.note}
                          onChange={e => update(l.id, 'note', e.target.value)}
                          rows={5}
                          style={{
                            width: '100%',
                            padding: '16px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border)',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            color: 'var(--text-heading)',
                            background: 'var(--surface)',
                            resize: 'vertical',
                            transition: 'border-color var(--transition)',
                            outline: 'none',
                            lineHeight: 1.7,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* ── Actions ── */}
        <div className="al-actions-bar">
          <button
            className="al-add-more-btn"
            onClick={addLearner}
            disabled={learners.length >= MAX_LEARNERS}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Another Applicant
            {learners.length >= MAX_LEARNERS && <span style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.75rem' }}>(Max {MAX_LEARNERS})</span>}
          </button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-ghost" onClick={() => navigate(returnUrl)}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <svg width="15" height="15" className="al-spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Saving…
                </>
              ) : (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save {learners.length > 1 ? `${learners.length} Applicants` : 'Applicant'}
                </>
              )}
            </button>
          </div>
        </div>

      </motion.div>
    </>
  );
};

export default AddLearnerPage;
